/**
 * Datos de la pantalla "Tu agente" (/agente): direcciones, pasos para conectar
 * ChatGPT y lo que la documentación oficial de OpenAI dice sobre qué planes
 * pueden agregar conectores MCP propios.
 *
 * Las direcciones y los pasos de ChatGPT también viven en
 * `public/guia-agente/conectar.html` (página pública con QR, también para
 * Claude y Cursor). `src/__tests__/unit/agenteConexionParidad.test.js` falla si
 * la dirección del conector o la fecha de verificación de los pasos dejan de
 * coincidir entre las dos, para que no haya dos verdades.
 *
 * @module config/agenteConexion
 */

export const KANE_URL = process.env.REACT_APP_KANE_URL || 'https://kane-onedeck.vercel.app';

/** Dirección del conector MCP que se pega en ChatGPT. */
export const MCP_CONNECTOR_URL =
  'https://mcp-server-orcin.vercel.app/api/mcp/v1';

/** Los pasos de `conectar.html` se verificaron a mano en ChatGPT web en esta fecha. */
export const PASOS_VERIFICADOS_EL = '9 de septiembre de 2026';

/** Fecha en que se leyeron las fuentes oficiales de abajo. */
export const FUENTES_CONSULTADAS_EL = '7 de octubre de 2026';

export const PASOS_CHATGPT = [
  {
    titulo: 'Abre ChatGPT en un computador',
    detalle:
      'Entra a chatgpt.com desde el navegador. Según OpenAI, estas apps de MCP funcionan sólo en la web, no en la app del teléfono.',
  },
  {
    titulo: 'Activa el modo desarrollador',
    detalle:
      'Toca tu nombre, entra a Configuración → Plugins (en algunas cuentas todavía se llama Apps) y enciende Developer mode. En un plan Business esto sólo lo puede hacer un administrador o propietario del espacio de trabajo.',
  },
  {
    titulo: 'Crea el conector',
    detalle:
      'En Plugins toca el botón de más, elige Add custom MCP server (o Create app). Nombre: ProMedic. Pega la dirección de arriba. Autenticación: OAuth. Marca «I understand and want to continue» y toca Create.',
  },
  {
    titulo: 'Entra con tu cuenta de siempre',
    detalle:
      'Se abre el login de ProMedic. Usa tu correo y contraseña de siempre. Tu contraseña la escribes tú, en esa ventana; el agente nunca la ve.',
  },
  {
    titulo: 'Pruébalo',
    detalle:
      'En un chat nuevo escribe @ProMedic y pregunta: «¿qué puedes hacer por mí?». Luego prueba algo real: «¿cuáles son mis proyectos?». Debe responder con tus datos reales y según tu rol.',
  },
];

/**
 * Qué planes pueden agregar un conector MCP propio. SÓLO afirma lo que decían
 * las páginas oficiales leídas el 7-oct-2026; lo que no estaba escrito va en
 * `sinConfirmar`, no en una lista de planes que sí pueden.
 */
export const PLANES_CHATGPT = {
  confirmado: [
    {
      plan: 'Business, Enterprise y Edu',
      puede:
        'Pueden crear y probar conectores MCP propios, incluidas acciones que escriben o modifican datos. Sólo en la web.',
      cita: 'Apps, full MCP support, and developer mode are available for ChatGPT Business and Enterprise/Edu customers on ChatGPT web.',
    },
    {
      plan: 'Pro',
      puede:
        'Puede conectar un MCP sólo con permisos de lectura y consulta (read/fetch) en modo desarrollador. No escribe.',
      cita: 'Pro users can connect MCPs with read/fetch permissions in developer mode.',
    },
  ],
  sinConfirmar: {
    planes: 'Free (gratis), Go y Plus',
    texto:
      'La documentación oficial que leímos no menciona estos planes entre los que pueden agregar conectores MCP propios. En la práctica, parte del equipo de ProMedic ya usa el agente con ChatGPT Plus, consultando y registrando; pero OpenAI no lo garantiza y puede cambiarlo. Si tu cuenta es gratuita, usa Kane OneDeck.',
  },
  minimo:
    'Para usar el agente completo desde ChatGPT (registrar horas, pedir materiales, subir fotos) el plan que la documentación respalda es ChatGPT Business, y ahí lo configura un administrador del espacio de trabajo. Con Plus nos funciona hoy, sin respaldo oficial. Si quieres ir a lo seguro, usa Kane OneDeck.',
};

export const FUENTES_OPENAI = [
  {
    titulo: 'Developer mode and MCP apps in ChatGPT',
    url: 'https://help.openai.com/en/articles/12584461-developer-mode-and-mcp-apps-in-chatgpt',
  },
  {
    titulo: 'Connect and test your plugin (OpenAI Developers)',
    url: 'https://developers.openai.com/plugins/deploy/connect-chatgpt',
  },
  {
    titulo: 'Connected apps in ChatGPT',
    url: 'https://help.openai.com/en/articles/11487775-connectors-in-chatgpt',
  },
];

/**
 * Otros asistentes que, según su documentación oficial (leída el
 * `FUENTES_CONSULTADAS_EL`), permiten agregar un servidor MCP remoto propio con
 * OAuth sin pagar. NO se probaron contra cuentas reales: los pasos son los de la
 * documentación. `plan.estado`:
 *  - 'si': la documentación dice que el plan gratuito puede.
 *  - 'sin-requisito': la documentación no pide un plan de pago, pero tampoco
 *    dice «gratis» con esas palabras (se avisa en la pantalla).
 */
export const OTROS_ASISTENTES = [
  {
    id: 'claude',
    nombre: 'Claude (claude.ai y Claude Desktop)',
    plan: {
      estado: 'si',
      texto: 'Plan gratuito: sí, con un solo conector propio. Pro, Max, Team y Enterprise también.',
    },
    pasos: [
      'Entra a claude.ai en el navegador (o abre Claude Desktop) con tu cuenta.',
      'Ve a Customize → Connectors. Toca «+ Add» y luego «Add custom connector».',
      'Nombre: ProMedic. Pega la dirección del conector y toca «Continue».',
      'Revisa la autenticación que detectó (OAuth), toca «Continue» y luego «Add».',
      'Inicia sesión con tu cuenta de ProMedic en la ventana que se abre. Prueba: «¿cuáles son mis proyectos?».',
    ],
    limites: [
      'En el plan gratuito sólo puedes tener un conector propio.',
      'Se agrega desde la web o el escritorio; las apps del teléfono sólo usan los conectores que ya agregaste.',
      'Puede consultar y también crear o modificar datos.',
    ],
    fuentes: [
      {
        titulo: 'Get started with custom connectors using remote MCP (Claude Help Center)',
        url: 'https://support.claude.com/en/articles/11175166-get-started-with-custom-connectors-using-remote-mcp',
      },
    ],
  },
  {
    id: 'mistral',
    nombre: 'Mistral Le Chat',
    plan: {
      estado: 'si',
      texto:
        'Plan gratuito: sí. La documentación dice que agregar conectores propios lo hace el administrador, y en Free, Pro y Student ese administrador eres tú, el dueño de la cuenta.',
    },
    pasos: [
      'Entra a tu cuenta de Mistral (Le Chat) y abre la página Connectors.',
      'Toca «+ Add Connector» y elige la pestaña «Custom MCP Connector».',
      'Pon un nombre corto sin espacios (por ejemplo promedic) y pega la dirección del conector.',
      'Toca «Connect». Mistral detecta solo el tipo de autenticación (OAuth 2.1).',
      'Inicia sesión con tu cuenta de ProMedic en la ventana de consentimiento.',
    ],
    limites: [
      'Para las funciones que modifican datos, Le Chat te pide aprobar cada vez; mantenlo así.',
      'La documentación actual llama «Work» a esta parte de Le Chat y no dice si el paso de agregar se puede hacer desde el teléfono. Hazlo en el navegador.',
    ],
    fuentes: [
      {
        titulo: 'MCP Connectors (Mistral docs)',
        url: 'https://docs.mistral.ai/le-chat/knowledge-integrations/connectors/mcp-connectors',
      },
      {
        titulo: 'Le Chat. Custom MCP connectors. Memories. (Mistral)',
        url: 'https://mistral.ai/news/le-chat-mcp-connectors-memories/',
      },
    ],
  },
  {
    id: 'gemini',
    nombre: 'Gemini (app web)',
    plan: {
      estado: 'sin-requisito',
      texto:
        'Plan gratuito: la ayuda de Google no pide un plan de pago, pero sí condiciones de cuenta y país (abajo). Léelas antes de intentar.',
    },
    pasos: [
      'Entra a gemini.google.com desde un computador con tu cuenta personal de Google.',
      'Toca Settings → Connected Apps (si no aparece, primero «Personal Intelligence»).',
      'En «Custom apps» toca «Add a custom app» y pega la dirección del conector.',
      'Si te pide credenciales, ábrelas en «Show more» de Advanced features. Toca «Next» y sigue las instrucciones, incluido el login de ProMedic.',
      'Para usarlo, escribe @ en el chat y elige ProMedic.',
    ],
    limites: [
      'Sólo para mayores de 18 años en Estados Unidos, y la función sólo está en inglés. Si estás en otro país, esta opción probablemente no te sirve.',
      'Cuentas de trabajo o escuela no funcionan, y «Keep Activity» debe estar activado.',
      'Se agrega sólo desde la web; después también funciona en la app del teléfono.',
      'Gemini pide confirmación manual en cada acción que escribe datos.',
    ],
    fuentes: [
      {
        titulo: 'Connect & manage custom apps for Gemini Apps (Gemini Apps Help)',
        url: 'https://support.google.com/gemini/answer/17209137?hl=en&co=GENIE.Platform%3DDesktop',
      },
    ],
  },
  {
    id: 'grok',
    nombre: 'Grok (xAI)',
    plan: {
      estado: 'sin-requisito',
      texto:
        'Plan gratuito: xAI dice que los conectores están disponibles para todos los usuarios de Grok y no limita el conector propio a un plan de pago, pero no lo afirma expresamente para el plan gratuito.',
    },
    pasos: [
      'Entra a grok.com/connectors con tu cuenta.',
      'Toca «New Connector» y elige «Custom».',
      'Pega la dirección del conector (es pública, sirve tal cual).',
      'Completa la autenticación que pida: inicia sesión con tu cuenta de ProMedic.',
    ],
    limites: [
      'La documentación no dice en qué plataformas (web o teléfono) se puede agregar; sólo da la dirección web grok.com/connectors.',
      'En Grok Business y Enterprise, el conector lo agrega un administrador del equipo.',
    ],
    fuentes: [
      { titulo: 'Connectors (xAI docs)', url: 'https://docs.x.ai/grok/connectors' },
      {
        titulo: 'Custom MCP Server Tunneling (xAI docs)',
        url: 'https://docs.x.ai/grok/connectors/custom-mcp-tunneling',
      },
    ],
  },
];

/** Los que NO sirven gratis, o no se pudo confirmar. Una línea cada uno, sin pasos. */
export const ASISTENTES_NO_DISPONIBLES = [
  {
    nombre: 'Meta Muse (Meta AI)',
    motivo:
      'La ayuda de Meta describe «conectores personalizados» que se crean pidiéndoselo a Muse, y no menciona MCP. El acceso a MCP de Meta es una vista previa limitada para desarrolladores. No hay forma documentada de conectar nuestra dirección.',
    url: 'https://www.meta.com/help/artificial-intelligence/1687253048996149/',
  },
  {
    nombre: 'Perplexity',
    motivo:
      'Sin confirmar. Según su registro de cambios (lo vimos en el buscador; sus páginas dieron error al abrirlas), los conectores propios son para Pro, Max y Enterprise, no para el plan gratuito.',
    url: 'https://www.perplexity.ai/help-center/en/articles/13915507-adding-custom-remote-connectors',
  },
  {
    nombre: 'Microsoft Copilot',
    motivo:
      'Sin confirmar. No encontramos documentación oficial para la app Copilot de uso personal. Lo documentado (Copilot Studio y Microsoft 365 Copilot) es para empresas y requiere un administrador.',
    url: 'https://learn.microsoft.com/en-us/microsoft-copilot-studio/mcp-add-existing-server-to-agent',
  },
  {
    nombre: 'Gemini CLI y Google AI Studio',
    motivo:
      'Gemini CLI es una herramienta de terminal y su documentación dice que para quienes no pagan fue reemplazada por Antigravity CLI el 18 de junio de 2026 (no lo verificamos). En AI Studio no hay documentado un menú para agregar MCP; el MCP remoto de Gemini se usa por API, con código.',
    url: 'https://geminicli.com/docs/tools/mcp-server/',
  },
];
